import { useQuery, useApolloClient } from '@apollo/client';
import { gql } from 'graphql-tag';
import { PAGE_SIZE } from '~/modules/common/const';
import { useMeContext } from '~/modules/me/useMeContext';

export const GET_PROGRAMS_QUERY = gql`
  query GetProgramsList($programFilter: ProgramFilter2, $pageSize: Int) {
    programs: programs2(
      page: 1
      pageSize: $pageSize
      programFilter: $programFilter
    ) {
      totalItems
      items {
        id
        displayText
      }
    }
  }
`;

export const GET_ACTIVE_PROJECT_PROGRAMS_QUERY = gql`
  query getPageOfProgramsAvailableForFilteringProjects(
    $pageSize: Int!
    $programFilter: ProgramFilter2
  ) {
    pageOfProgramsAvailableForFilteringProjects(
      page: 1
      pageSize: $pageSize
      programFilter: $programFilter
    ) {
      id
      displayText
    }
  }
`;

export const PAGE_OF_PROGRAMS_AVAILABLE_FOR_FILTERING_RESOURCE_USERS_QUERY = gql`
  query PageOfProgramsAvailableForFilteringResourceUsersSearch(
    $page: Int!
    $pageSize: Int!
    $programFilter: ProgramFilter2
  ) {
    pageOfProgramsAvailableForFilteringResourceUsers(
      page: $page
      pageSize: $pageSize
      programFilter: $programFilter
    ) {
      id
      displayText
    }
  }
`;

const mappedPrograms = programs =>
  (programs || []).map(program => ({
    ...program,
    value: program.id,
    label: program.displayText,
    key: program.displayText
  }));

const getFilter = searchTerm => {
  return { text: searchTerm.length > 0 ? searchTerm : null };
};

const usePageOfProgramsAvailableForFilteringResourceUsersSearch = ({
  searchTerm,
  skip
}) => {
  const { data, loading } = useQuery(
    PAGE_OF_PROGRAMS_AVAILABLE_FOR_FILTERING_RESOURCE_USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        page: 1,
        pageSize: PAGE_SIZE + 1,
        programFilter: { isActive: true, ...getFilter(searchTerm) }
      },
      context: {
        debounceKey: 'program-search-option',
        debounceTimeout: 250
      },
      skip
    }
  );

  return {
    programs: data?.pageOfProgramsAvailableForFilteringResourceUsers || [],
    loading
  };
};

export const useSearchablePrograms = (
  searchTerm,
  usePageOfProgramsAvailableForFilteringResourceUsersQuery = false
) => {
  const { permissionsMap } = useMeContext();

  const canViewProgram = Boolean(
    permissionsMap['urn:replicon:program-action:view-program']
  );

  const query = !canViewProgram
    ? GET_ACTIVE_PROJECT_PROGRAMS_QUERY
    : GET_PROGRAMS_QUERY;

  const { data, loading: projectScopedLoading } = useQuery(query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      pageSize: PAGE_SIZE + 1,
      programFilter: { isActive: true, ...getFilter(searchTerm) }
    },
    skip: usePageOfProgramsAvailableForFilteringResourceUsersQuery
  });

  const {
    programs: programsForFilteringResourceUsers,
    loading: programsForFilteringResourceUsersLoading
  } = usePageOfProgramsAvailableForFilteringResourceUsersSearch({
    searchTerm,
    skip: !usePageOfProgramsAvailableForFilteringResourceUsersQuery
  });

  const _programs = usePageOfProgramsAvailableForFilteringResourceUsersQuery
    ? programsForFilteringResourceUsers
    : (!canViewProgram
        ? data?.pageOfProgramsAvailableForFilteringProjects
        : data?.programs?.items) || [];

  const loading = usePageOfProgramsAvailableForFilteringResourceUsersQuery
    ? programsForFilteringResourceUsersLoading
    : projectScopedLoading;

  const programs = (mappedPrograms(_programs) || []).slice(0, PAGE_SIZE);

  return {
    programs,
    loading,
    hasMore: _programs.length > PAGE_SIZE
  };
};

export const searchPrograms = ({
  apolloClient
}) => setHasMore => async searchTerm => {
  const result = await apolloClient.query({
    query: GET_PROGRAMS_QUERY,
    variables: { pageSize: PAGE_SIZE, programFilter: getFilter(searchTerm) }
  });

  const programs = mappedPrograms(result?.data?.programs?.items);

  setHasMore(result?.data?.programs?.totalItems > programs.length);

  return programs;
};

const useSearchableProgramsWithHasMore = () => {
  const apolloClient = useApolloClient();

  return {
    searchPrograms: searchPrograms({
      apolloClient
    })
  };
};

export default useSearchableProgramsWithHasMore;

export const searchProgramsForFilteringResourceUsers = ({
  apolloClient
}) => setHasMore => async searchTerm => {
  const result = await apolloClient.query({
    query: PAGE_OF_PROGRAMS_AVAILABLE_FOR_FILTERING_RESOURCE_USERS_QUERY,
    variables: {
      page: 1,
      pageSize: PAGE_SIZE + 1,
      programFilter: { isActive: true, ...getFilter(searchTerm) }
    }
  });

  const allPrograms = mappedPrograms(
    result?.data?.pageOfProgramsAvailableForFilteringResourceUsers
  );
  const programs = allPrograms.slice(0, PAGE_SIZE);

  setHasMore(allPrograms.length > PAGE_SIZE);

  return programs;
};

export const useSearchableProgramsForFilteringResourceUsers = () => {
  const apolloClient = useApolloClient();

  return {
    searchPrograms: searchProgramsForFilteringResourceUsers({ apolloClient })
  };
};
